import React from "react";

import { BSCard } from "traec-react/utils/bootstrap";

import useApi, { doFetch } from "storybook-dashboard/utils/fetching";
import { Modal, useModal } from "storybook-dashboard/components/modal";
import DataTable from "react-data-table-component";
import { ActionIcon } from "./utils";
import { useProjectPermissions } from "storybook-dashboard/utils/hooks";
import { Hide } from "../../utils";
import Octicon from "react-octicon";
import Im from "immutable";

const repsondInvite = ({ projectId, item, triggerInvites, triggerMembers, accept }) => {
  console.log("sendProjectInvite");
  let status = accept ? "accepted" : "rejected";
  let inviteId = item.get("uid");
  doFetch(`/api/project/${projectId}/invite/${inviteId}/`, "PUT", { status }).then(() => {
    if (triggerInvites) triggerInvites();
    if (triggerMembers) triggerMembers();
  });
};

const deleteInvite = ({ projectId, item, triggerInvites }) => {
  let inviteId = item.get("uid");
  doFetch(`/api/project/${projectId}/invite/${inviteId}/`, "DELETE").then(() => {
    if (triggerInvites) triggerInvites();
  });
};

function DeleteInvitationConfirmationModal({ invitation, onConfirm, onCancel }) {
  const email = invitation.get("email", "");
  const role = invitation.getIn(["project_discipline", "name"], "No role assigned");

  return (
    <Modal title="Delete Invitation">
      <div className="p-3">
        <div className="d-flex align-items-start mb-3">
          <div className="text-warning me-3 mt-1">
            <Octicon name="alert" style={{ fontSize: "24px" }} />
          </div>
          <div>
            <h6 className="mb-2">Are you sure you want to delete this invitation?</h6>
            <p className="text-muted mb-3">
              This action will permanently delete the invitation. The invited user will not be able to join the project
              using this invitation link.
            </p>
          </div>
        </div>

        <div className="card bg-light border-0 mb-3">
          <div className="card-body py-3">
            <div className="row">
              <div className="col-sm-6">
                <small className="text-muted d-block">Email</small>
                <strong>{email}</strong>
              </div>
              <div className="col-sm-6">
                <small className="text-muted d-block">Invited Role</small>
                <span className="badge bg-light text-dark">{role}</span>
              </div>
            </div>
          </div>
        </div>

        <div className="alert alert-warning d-flex align-items-start">
          <Octicon name="info" className="me-2 mt-1" style={{ fontSize: "16px" }} />
          <div>
            <strong>Note:</strong> If you need to invite this user again, you'll need to send a new invitation with
            their email address.
          </div>
        </div>

        <div className="d-flex gap-2 justify-content-end">
          <button className="btn btn-secondary" onClick={onCancel}>
            Cancel
          </button>
          <button className="btn btn-danger" onClick={onConfirm}>
            <Octicon name="trashcan" className="me-2" />
            Delete Invitation
          </button>
        </div>
      </div>
    </Modal>
  );
}

function ProjectInviteHeader({ projectId, inviteCount }) {
  const { data: project } = useApi(`/api/project/{projectId}/`, { projectId });

  return (
    <div className="card shadow-sm mb-4">
      <div className="card-body">
        <div className="d-flex justify-content-between align-items-start">
          <div>
            <div className="d-flex align-items-center mb-2">
              <h1 className="h3 mb-0 text-primary">Project Invitations</h1>
            </div>
            <p className="text-muted mb-2">{project?.get("name")}</p>
            <p className="text-muted small mb-0">
              <strong>Pending invitations:</strong> {inviteCount}
            </p>
          </div>
          <div className="text-end">
            <span className="badge badge-primary">Invitation Management</span>
          </div>
        </div>
      </div>
    </div>
  );
}

function InviteDescription() {
  return (
    <div className="mb-4">
      <h2 className="h4 text-dark mb-2">Invitation Management</h2>
      <p className="text-muted">
        Manage project invitations that have been sent to users. You can accept invitations on behalf of users or remove
        invitations that are no longer needed.
      </p>
      <div className="alert alert-info">
        <div className="d-flex">
          <Octicon name="mail" className="me-2 mt-1" />
          <div>
            <strong>Invitation Status:</strong> These are invitations that have been sent but not yet accepted by users.
            You can accept them on behalf of users or delete them if they're no longer needed.
          </div>
        </div>
      </div>
    </div>
  );
}

const getColumns = (props) => [
  {
    name: "Email",
    sortable: true,
    selector: (item) => item.get("email"),
    grow: 2,
    wrap: true,
    cell: (item) => <span className="text-muted">{item.get("email")}</span>,
  },
  {
    name: "Role",
    sortable: true,
    selector: (item) => item.getIn(["project_discipline", "name"]),
    grow: 1,
    wrap: true,
    cell: (item) => {
      const roleName = item.getIn(["project_discipline", "name"]);
      return roleName ? (
        <span className="badge bg-light text-dark">{roleName}</span>
      ) : (
        <span className="text-muted">No role assigned</span>
      );
    },
  },
  {
    name: "Actions",
    cell: (item) => (
      <Hide when={!props?.isAdmin}>
        <div className="d-flex gap-1">
          <button
            className="btn btn-sm btn-success"
            onClick={() => repsondInvite({ ...props, item, accept: true })}
            title="Accept invitation on behalf of user"
          >
            <Octicon name="check" />
          </button>
          <button
            className="btn btn-sm btn-outline-danger"
            onClick={() => props.onDeleteInvite && props.onDeleteInvite(item)}
            title="Delete invitation"
          >
            <Octicon name="trashcan" />
          </button>
        </div>
      </Hide>
    ),
    ignoreRowClick: true,
    allowOverflow: true,
    button: true,
    width: "120px",
  },
];

export default function InviteList({ projectId }) {
  let { setModal, hideModal } = useModal();
  let { isAdmin } = useProjectPermissions();
  let { trigger: triggerMembers } = useApi("/api/project/{projectId}/member/", { projectId });
  let { data: invites, trigger: triggerInvites, isLoading } = useApi("/api/project/{projectId}/invite/", { projectId });

  const inviteCount = invites?.size || 0;

  const handleDeleteInvite = (invitation) => {
    setModal(
      <DeleteInvitationConfirmationModal
        invitation={invitation}
        onConfirm={() => {
          deleteInvite({ projectId, item: invitation, triggerInvites });
          hideModal();
        }}
        onCancel={hideModal}
      />
    );
  };

  return (
    <div className="container-fluid py-4">
      <div className="row">
        <div className="col-12">
          <ProjectInviteHeader projectId={projectId} inviteCount={inviteCount} />

          <div className="card shadow-sm">
            <div className="card-body">
              <InviteDescription />

              {isLoading ? (
                <div className="d-flex justify-content-center align-items-center py-5">
                  <div className="spinner-border text-primary me-3 mr-3" role="status">
                    <span className="sr-only">Loading...</span>
                  </div>
                  <span className="text-muted">Loading invitations...</span>
                </div>
              ) : (
                <div className="table-responsive">
                  <DataTable
                    columns={getColumns({
                      projectId,
                      triggerMembers,
                      triggerInvites,
                      isAdmin,
                      onDeleteInvite: handleDeleteInvite,
                    })}
                    data={invites?.toArray() || []}
                    highlightOnHover={true}
                    pagination={inviteCount > 10}
                    paginationPerPage={10}
                    noDataComponent={
                      <div className="text-center py-5">
                        <Octicon name="mail" className="text-muted mb-3" style={{ fontSize: "48px" }} />
                        <h5 className="text-muted">No pending invitations</h5>
                        <p className="text-muted">
                          All invitations have been processed or no invitations have been sent.
                        </p>
                      </div>
                    }
                    customStyles={{
                      table: {
                        style: {
                          borderRadius: "8px",
                        },
                      },
                      headRow: {
                        style: {
                          backgroundColor: "#f8f9fa",
                          borderTopLeftRadius: "8px",
                          borderTopRightRadius: "8px",
                        },
                      },
                      rows: {
                        style: {
                          "&:hover": {
                            backgroundColor: "#f8f9fa",
                          },
                        },
                      },
                    }}
                  />
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
